import { AgsAlternateAnimateBehaviour } from "ags/behaviours/ags-alternate-animate-behaviour";
import { AgsAnimateBehaviour } from "ags/behaviours/ags-animate-behaviour";
import { bigWinCommand } from "ags/commands/bigwin-command";
import { SparkResult } from "ags/integration/model/results/spark-result";
import { BigWinThresholds, winMagnitude } from "ags/utils/win-magnitude";
import { AbstractComponent } from "appworks/components/abstract-component";
import { Components } from "appworks/components/components";
import { CanvasService } from "appworks/graphics/canvas/canvas-service";
import { ButtonEvent } from "appworks/graphics/elements/button-element";
import { gameState } from "appworks/model/game-state";
import { Services } from "appworks/services/services";
import { stateMachine } from "appworks/state-machine/state-machine";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { reject } from "appworks/utils/collection-utils";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { deviceInfo } from "appworks/utils/device-info";
import { MatrixComponent } from "slotworks/components/matrix/matrix-component";
import { SymbolSubcomponent } from "slotworks/components/matrix/symbol/symbol-subcomponent";
import { BonusResult } from "slotworks/model/gameplay/records/results/bonus-result";
import { SymbolWinResult } from "slotworks/model/gameplay/records/results/symbol-win-result";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";
import { AutoplayService } from "slotworks/services/autoplay/autoplay-service";
import { SlotBetService } from "slotworks/services/bet/slot-bet-service";

export class AnimateWinsComponent extends AbstractComponent {

    public static AgsAnimateBehaviourType = AgsAnimateBehaviour;

    protected showingBigWin: boolean = false;
    protected bigWinSkipListener: () => void;

    constructor(protected alternateAnimation = true) {
        super();
    }

    public startAnimating(winnings: number,fromExitFreeSpin:boolean = false): void {
        const winRatio = winnings / Services.get(SlotBetService).getTotalStake();
        const bigWinLevel = winMagnitude.getBigWinLevel(winRatio);

        const winOverAnimationThreshold = (bigWinLevel >= BigWinThresholds.REGULAR && this.currentRecordHasSymbolWins());
        const hasBonus = this.currentRecordHasBonusWin();
        const shouldAnimateWinningSymbols = winOverAnimationThreshold || hasBonus;

        if (bigWinLevel >= BigWinThresholds.BIG) {
            this.showingBigWin = true;
            bigWinCommand.start(bigWinLevel);

            if (!deviceInfo.isDesktop) { // PP-241 big win should skip in mobile autoplay if screen tapped
                this.bigWinSkipListener = () => {
                    this.endBigWinAnimation(true).execute();
                    if(!Services.get(AutoplayService).isAutoPlaying()) {
                        // We don't want to skip when autoplay is active because this will queue the next spin
                        stateMachine.skip();
                    }
                 };
                Services.get(CanvasService).rootNode.addEventListener(ButtonEvent.CLICK.getDOMEventString(), this.bigWinSkipListener);
            }
        }

        if (shouldAnimateWinningSymbols) {
            this.animateWinningSymbols();
        }

        uiFlags.set(UIFlag.IDLE, false);
    }

    public endBigWinAnimation(immediateStop = false): Contract<void> {
        if (this.bigWinSkipListener) {
            Services.get(CanvasService).rootNode.removeEventListener(ButtonEvent.CLICK.getDOMEventString(), this.bigWinSkipListener);
            this.bigWinSkipListener = null;
        }

        if (!this.showingBigWin) {
            return Contract.empty();
        }

        this.showingBigWin = false;

        return new Contract((resolve) => {
            this.stopBigWinAnimation(immediateStop).then(() => {
                resolve(null);
            });
        });
    }

    public end(): void {
        const symbols = Components.get(MatrixComponent).getAllSymbols();
        symbols.forEach((symbol) => {
            symbol.static();
            symbol.removeBehaviourOfType(AnimateWinsComponent.AgsAnimateBehaviourType);
            symbol.removeBehaviourOfType(AgsAlternateAnimateBehaviour);
        });
    }

    public animateWinningSymbols(): void {
        this.dimNonWinningSymbols();
        this.setSymbolBehaviours();

        const winningSymbols = this.getAllWinningSymbols();
        winningSymbols.forEach((symbol) => {
            symbol.win().execute();
        });
    }

    protected getAllWinningSymbols(): SymbolSubcomponent[] {
        const winningSymbols: SymbolSubcomponent[] = [];
        const wins = this.getAllWins();

        wins.forEach((win) => {
            for (const position of win.positions) {
                const symbol = Components.get(MatrixComponent).getSymbol(position.x, position.y);
                winningSymbols.push(symbol);
            }
        });

        return winningSymbols;
    }

    protected currentRecordHasSymbolWins(): boolean {
        const symbolWins = gameState.getCurrentGame().getCurrentRecord().getResultsOfType(SymbolWinResult);
        return symbolWins.length > 0;
    }

    protected currentRecordHasBonusWin(): boolean {
        const currentRecord = gameState.getCurrentGame().getCurrentRecord();
        const hasBonusWin = currentRecord.getResultsOfType(BonusResult).length > 0;
        const hasSpark = currentRecord.getResultsOfType(SparkResult).length > 0;
        return hasBonusWin && hasSpark;
    }

    protected dimNonWinningSymbols(): void {
        const dimSymbols: SymbolSubcomponent[] = reject(
            Components.get(MatrixComponent).getAllSymbols(),
            this.getAllWinningSymbols()
        );

        new Parallel(dimSymbols.map(symbol => {
            return () => symbol.cycleDim();
        })).execute();
    }

    protected stopBigWinAnimation(immediateStop = false): Contract<void> {
        return bigWinCommand.stop(immediateStop);
    }

    protected setSymbolBehaviours(): void {
        const wins = this.getAllWins();
        wins.forEach((win) => {
            for (const position of win.positions) {
                const symbol = Components.get(MatrixComponent).getSymbol(position.x, position.y);

                symbol.removeBehaviourOfType(AnimateWinsComponent.AgsAnimateBehaviourType);
                symbol.removeBehaviourOfType(AgsAlternateAnimateBehaviour);

                if (this.alternateAnimation) {
                    symbol.addBehaviour(new AgsAlternateAnimateBehaviour(symbol));
                } else {
                    symbol.addBehaviour(new AnimateWinsComponent.AgsAnimateBehaviourType(symbol));
                }
            }

            if (this.alternateAnimation) {
                this.alternateAnimation = !this.alternateAnimation;
            }
        });
    }

    protected getAllWins(): SymbolWinResult[] {
        const record = gameState.getCurrentGame().getCurrentRecord() as SpinRecord;
        const symbolWins = record.getResultsOfType(SymbolWinResult);
        const bonusWins = record.getResultsOfType(BonusResult).length ? record.getResultsOfType(SparkResult) : [];
        return [...symbolWins, ...bonusWins] as SymbolWinResult[];
    }
}
